import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useInRouterContext, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AuthFrame from "./AuthFrame";
import { Lockup, SocialButtons, Divider, Input, PasswordInput, Field, Note, Actions, PrimaryBtn, SecondaryBtn, TextBtn, InlineLink, CodeInput } from "./pieces";
import GoogleButton from "./GoogleButton";
import { loginWithPassword, sendPhoneLoginCode, verifyPhoneLoginCode, startSession, homePathFor } from "./authV2Services";
import { formatPhone, usPhoneDigits } from "./phoneFormat";

// The public athlete index, open without an account.
export const PUBLIC_INDEX_PATH = "/p/business/dashboard";

// Why the last session stopped, for example an admin locked the account.
// useAuthSync puts the plain message in the route state; it stays on screen
// here, not only in a toast that fades.
const SessionEndedNote = () => {
  const { state } = useLocation();
  const message = typeof state?.sessionEnded === "string" ? state.sessionEnded.trim() : "";
  return message ? <Note tone="critical">{message}</Note> : null;
};

export const NETWORK_ERROR = "We could not reach CACHE. Try again.";

// The account has no password it can use yet: it was opened with a mobile
// code and its email is not confirmed, or the email's owner took it over and
// the old password was cleared. Forgot password is the way in, so the
// refusal offers it.
export const needsPasswordReset = (data) => data?.error === "email_unproven" || data?.reason === "no_password";

// 01a · Log in. Email and password, or the mobile number as an alternate id
// (a Twilio Verify code). Keeps every branch the old form handled.
const LoginPage = () => {
  const navigate = useNavigate();
  const inRouter = useInRouterContext();
  const dispatch = useDispatch();
  const [mode, setMode] = useState("password"); // password | phone | phoneCode
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [viaEmail, setViaEmail] = useState(false);
  const [error, setError] = useState("");
  const [offerReset, setOfferReset] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setError(""); setOfferReset(false); }, [mode]);

  const finish = async (data) => {
    await startSession(data, dispatch);
    toast.success("You're in.");
    if (data.mustChangePassword) { navigate("/change-password?firstLogin=1"); return; }
    navigate(homePathFor(data.permissions || []));
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    const em = email.trim().toLowerCase();
    if (!em || !password) { setError("Enter your email and password."); return; }
    setBusy(true); setError(""); setOfferReset(false);
    try {
      const { ok, status, data } = await loginWithPassword({ email: em, password });
      if (status === 403 && data?.error === "email_unverified") {
        // The server says whether a link actually went out.
        toast.info(data?.message || "Please confirm your email first.");
        navigate("/check-email", { state: { email: em } });
        return;
      }
      if (status === 403 && data?.error === "pilot_cohort") {
        toast.info("This email is from our pilot. Create a fresh account to keep using CACHE.", { duration: 5000 });
        navigate(`/signup/email?email=${encodeURIComponent(em)}`);
        return;
      }
      if (!ok) {
        setError(data?.message || data?.error || data?.detail || "We could not log you in.");
        setOfferReset(needsPasswordReset(data));
        return;
      }
      await finish(data);
    } catch {
      setError(NETWORK_ERROR);
    } finally { setBusy(false); }
  };

  const sendCode = async () => {
    setBusy(true); setError("");
    try {
      const { ok, data } = await sendPhoneLoginCode({ phone });
      if (!ok) { setError(data?.message || "We could not send the code."); return; }
      setSentTo(data.to || phone); setViaEmail(data.channel === "email"); setCode(""); setMode("phoneCode");
    } catch {
      setError(NETWORK_ERROR);
    } finally { setBusy(false); }
  };

  const verifyCode = async () => {
    setBusy(true); setError("");
    try {
      const { ok, data } = await verifyPhoneLoginCode({ phone, code });
      if (!ok) { setError(data?.message || "That code is not right."); return; }
      await finish(data);
    } catch {
      setError(NETWORK_ERROR);
    } finally { setBusy(false); }
  };

  // On phones (Figma 1745:1337) the header's right slot reads "Browse" and
  // keeps the full name (decision D-o2), the lockup sits 8px under the
  // header, and the password form fills the column so its Actions drop to
  // the bottom of the screen as the sticky footer.
  return (
    <AuthFrame
      title="Log in"
      onBack={() => navigate(-1)}
      action={{
        label: "Browse the Player Index",
        pill: true,
        phoneLabel: "Browse",
        ariaLabel: "Browse the Player Index",
        onClick: () => navigate(PUBLIC_INDEX_PATH),
      }}
      phoneTop="lockup"
    >
      <Lockup tagline="Welcome back" />
      {inRouter && <SessionEndedNote />}
      <SocialButtons label="Log in with" enabled={process.env.REACT_APP_SOCIAL_SIGNIN === "on"} leading={<GoogleButton label="Google" />} />
      <Divider />
      {mode === "password" && (
        <form onSubmit={submitPassword} className="flex flex-col gap-[28px] max-sm:flex-1 max-sm:gap-[18px]">
          <div className="flex flex-col gap-3">
            <Input type="email" autoComplete="email" placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email" />
            <PasswordInput autoComplete="current-password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} aria-label="Password" />
            <div className="flex justify-end">
              <button type="button" onClick={() => navigate("/forgot-password")} className="bm-label-l text-bm-orange hover:underline">Forgot password?</button>
            </div>
            {error && (
              <Note tone="critical">
                {error}
                {offerReset && (
                  <>
                    {" "}
                    <button type="button" onClick={() => navigate("/forgot-password")} className="bm-label-l underline">Forgot password</button>
                  </>
                )}
              </Note>
            )}
          </div>
          <Actions>
            <PrimaryBtn type="submit" loading={busy}>Log in</PrimaryBtn>
            <InlineLink text="New to Cache?" label="Create an account" onClick={() => navigate("/register")} />
            <TextBtn onClick={() => setMode("phone")}>Log in with my mobile number</TextBtn>
          </Actions>
        </form>
      )}
      {mode === "phone" && (
        <>
          <Field label="Mobile number">
            <Input id="phone" inputMode="tel" autoComplete="tel" placeholder="(123)-456-7890" value={formatPhone(phone)} onChange={(e) => setPhone(usPhoneDigits(e.target.value))} />
          </Field>
          <Note>We text a 6-digit code to the mobile number on your account.</Note>
          {error && <Note tone="critical">{error}</Note>}
          <Actions>
            <PrimaryBtn onClick={sendCode} loading={busy} disabled={phone.length !== 10}>Send code</PrimaryBtn>
            <SecondaryBtn onClick={() => setMode("password")}>Use my password instead</SecondaryBtn>
          </Actions>
        </>
      )}
      {mode === "phoneCode" && (
        <>
          <p className="bm-body-l text-bm-muted max-sm:break-words">Sent to {sentTo}. It expires in 10 minutes.</p>
          {viaEmail && <Note>We could not text you right now, so the code went to the email on your account.</Note>}
          <CodeInput value={code} onChange={setCode} error={!!error} />
          {error && <p className="bm-body-m text-bm-critical">{error}</p>}
          <Actions>
            <PrimaryBtn onClick={verifyCode} loading={busy} disabled={code.length !== 6}>Verify</PrimaryBtn>
            <SecondaryBtn onClick={sendCode} disabled={busy}>Resend code</SecondaryBtn>
            <TextBtn onClick={() => setMode("password")}>Use my password instead</TextBtn>
          </Actions>
        </>
      )}
    </AuthFrame>
  );
};

export default LoginPage;
